import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, TrashIcon, EyeIcon, EyeSlashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService, unitApi } from '../utils/api';
import { useUser } from '../contexts/UserContext';
import { P } from '../types/assistantPrivileges';

interface Unit {
  id: number;
  title: string;
  lessons: Lesson[];
  isVisible: boolean;
  gradeName?: string;
  teacherName?: string;
}

interface Lesson {
  id: number;
  title: string;
  type: 'video' | 'quiz' | 'file' | 'assignment';
  isVisible: boolean;
  order?: number;
}

interface Teacher {
  id: string;
  fullName: string;
  email: string;
  active: boolean;
}

interface Grade {
  id: string;
  name: string;
  description: string;
  level: number;
  code: string;
  active: boolean;
}

const Lectures = () => {
  const navigate = useNavigate();
  const { user, isTeacher, isModerator, getTeacherId, hasPrivilege } = useUser();
  const canAddUnit = hasPrivilege(P.Unit_Add);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    unitName: '',
    grade: '',
    teacherId: '',
  });
  
  // Modal states
  const [showAddUnitModal, setShowAddUnitModal] = useState(false);
  const [newUnitTitle, setNewUnitTitle] = useState('');
  const [newUnitSubtitle, setNewUnitSubtitle] = useState('');
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [selectedGradeId, setSelectedGradeId] = useState('');
  const [isIndividual, setIsIndividual] = useState(true);
  const [newUnitPrice, setNewUnitPrice] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  const resetAddUnitForm = () => {
    setNewUnitTitle('');
    setNewUnitSubtitle('');
    setSelectedTeacherId('');
    setSelectedGradeId('');
    setIsIndividual(true);
    setNewUnitPrice('');
    setImageFile(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
  };

  const openAddUnitModal = () => {
    resetAddUnitForm();
    if (isTeacher()) {
      const teacherId = getTeacherId();
      if (teacherId) setSelectedTeacherId(teacherId);
    }
    setShowAddUnitModal(true);
  };
  
  // Modal states for delete and toggle visibility
  const [unitToDelete, setUnitToDelete] = useState<Unit | null>(null);
  const [unitToToggle, setUnitToToggle] = useState<Unit | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch grades
  const fetchGrades = useCallback(async () => {
    try {
      const response = await authFetch(apiUrl('Grade'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGrades(data.data);
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
      setGrades([]);
    }
  }, []);

  // Fetch units without course
  const fetchUnits = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const teacherId = isTeacher() ? getTeacherId() : null;
      const teacherParam = teacherId ? `&teacherId=${teacherId}` : '';
      const _nameParam = filters.unitName ? `&name=${encodeURIComponent(filters.unitName)}` : '';
      const _gradeParam = filters.grade ? `&gradeId=${encodeURIComponent(filters.grade)}` : '';
      const teacherFilterParam = filters.teacherId ? `&teacherId=${encodeURIComponent(filters.teacherId)}` : '';
      
      const response = await authFetch(
        apiUrl(`Unit/without-course?pageNumber=${currentPage}&pageSize=${pageSize}${teacherParam}${teacherFilterParam}`),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        // The response structure is: data.data.data (pagination wrapper)
        const paginationData = data.data;
        const unitsArray = paginationData?.data || [];
        
        // Transform the data to match our interface
        const transformedUnits = unitsArray.map((unit: any) => ({
          id: unit.id,
          title: unit.unitName || unit.titel || unit.unitTitle,
          lessons: unit.lessons?.map((lesson: any) => ({
            id: lesson.id,
            title: lesson.lessonName || lesson.titel,
            type: lesson.type,
            isVisible: lesson.active,
            order: lesson.order
          })) || [],
          isVisible: unit.active,
          gradeName: unit.gradeName || unit.grade || 'غير محدد',
          teacherName: unit.teacherName || unit.teacher || 'غير محدد'
        }));

        setUnits(transformedUnits);
        setTotalCount(paginationData?.totalCount || 0);
        setTotalPages(paginationData?.totalPages || 1);
      } else {
        setError(data.message || 'فشل في تحميل المحاضرات');
      }
    } catch (err: any) {
      console.error('Error fetching units:', err);
      setError(err.message || 'حدث خطأ أثناء تحميل المحاضرات');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters.unitName, filters.grade, filters.teacherId, isTeacher, getTeacherId]);

  useEffect(() => {
    fetchGrades();
  }, [fetchGrades]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  // Fetch teachers
  const fetchTeachers = useCallback(async () => {
    try {
      setLoadingTeachers(true);
      const response = await authFetch(
        apiUrl('Teacher?active=true'),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        setTeachers(data.data || []);
      }
    } catch (err: any) {
      console.error('Error fetching teachers:', err);
    } finally {
      setLoadingTeachers(false);
    }
  }, []);

  useEffect(() => {
    if (showAddUnitModal || isModerator()) {
      fetchTeachers();
    }
  }, [showAddUnitModal, isModerator, fetchTeachers]);

  // Handle search/filter
  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({
      unitName: '',
      grade: '',
      teacherId: '',
    });
    setCurrentPage(1);
  };

  // Handle add unit
  const handleAddUnit = async () => {
    if (!newUnitTitle.trim()) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إدخال عنوان الوحدة'
      });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    if (!selectedGradeId) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى اختيار المستوى الدراسي'
      });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    const priceValue = parseFloat(newUnitPrice);
    if (newUnitPrice.trim() === '' || Number.isNaN(priceValue) || priceValue < 0) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى إدخال سعر صحيح (0 أو أكثر)'
      });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    const teacherId = isTeacher() ? (getTeacherId() || selectedTeacherId) : selectedTeacherId;
    if (!teacherId) {
      setSaveMessage({
        type: 'error',
        text: 'يرجى اختيار المعلم'
      });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    try {
      setIsSaving(true);

      let imagePath: string | undefined;
      if (imageFile) {
        const uploadResponse = await unitApi.uploadImage(imageFile);
        if (!uploadResponse.success) {
          setSaveMessage({
            type: 'error',
            text: uploadResponse.message || 'فشل رفع صورة المحاضرة'
          });
          return;
        }
        imagePath = uploadResponse.data?.imagePath;
      }

      const unitName = newUnitTitle.trim();
      const titel = newUnitSubtitle.trim() || unitName;

      const response = await authFetch(apiUrl('Unit/add'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          unitName,
          titel,
          active: true,
          isIndividual,
          teacherId,
          gradeId: selectedGradeId,
          price: priceValue,
          ...(imagePath ? { imagePath } : {})
        })
      });

      const data = await response.json();

      if (data.success) {
        setSaveMessage({
          type: 'success',
          text: 'تم إضافة الوحدة بنجاح'
        });
        setShowAddUnitModal(false);
        resetAddUnitForm();
        fetchUnits(); // Refresh the list
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في إضافة الوحدة'
        });
      }
    } catch (err: any) {
      setSaveMessage({
        type: 'error',
        text: err.message || 'حدث خطأ أثناء إضافة الوحدة'
      });
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };


  // Handle delete unit
  const handleDeleteUnit = async (unitId: number) => {
    setIsDeleting(true);
    
    try {
      const response = await authFetch(apiUrl(`Unit/${unitId}`), {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSaveMessage({
          type: 'success',
          text: 'تم حذف المحاضرة بنجاح'
        });
        setUnitToDelete(null);
        fetchUnits(); // Refresh the list
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في حذف المحاضرة'
        });
      }
    } catch (error) {
      console.error('Error deleting unit:', error);
      setSaveMessage({
        type: 'error',
        text: 'حدث خطأ أثناء حذف المحاضرة'
      });
    } finally {
      setIsDeleting(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // Handle toggle unit visibility
  const handleToggleVisibility = async (unit: Unit) => {
    try {
      const newVisibility = !unit.isVisible;
      
      const response = await authFetch(apiUrl(`Unit/${unit.id}/toggle-visibility`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          isActive: newVisibility
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSaveMessage({
          type: 'success',
          text: `تم ${newVisibility ? 'إظهار' : 'إخفاء'} المحاضرة بنجاح`
        });
        setUnitToToggle(null);
        fetchUnits(); // Refresh the list
      } else {
        setSaveMessage({
          type: 'error',
          text: data.message || 'فشل في تحديث حالة المحاضرة'
        });
      }
    } catch (error) {
      console.error('Error toggling unit visibility:', error);
      setSaveMessage({
        type: 'error',
        text: 'حدث خطأ أثناء تحديث حالة المحاضرة'
      });
    } finally {
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                المحاضرات
              </h1>
              <p className="mt-2 text-gray-600">إدارة المحاضرات والدروس المستقلة</p>
            </div>
            {canAddUnit && (
            <button
              onClick={openAddUnitModal}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg 
                         shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 
                         focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="w-5 h-5 ml-2" />
              إضافة محاضرة
            </button>
            )}
          </div>
        </div>

        {/* Success/Error Message */}
        {saveMessage && (
          <div className={`mb-4 p-4 rounded-lg ${
            saveMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {saveMessage.text}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 transition-all duration-200 hover:shadow-xl">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1">
              <label htmlFor="name-filter" className="block text-sm font-medium text-gray-700">
                اسم المحاضرة
              </label>
              <input
                type="text"
                id="name-filter"
                value={filters.unitName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, unitName: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
                placeholder="البحث في المحاضرات..."
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="grade-filter" className="block text-sm font-medium text-gray-700">
                المستوى الدراسي
              </label>
              <select
                id="grade-filter"
                value={filters.grade}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, grade: e.target.value })}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
              >
                <option value="">جميع المستويات</option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Teacher Filter - Only for Moderators */}
            {isModerator() && (
              <div className="space-y-1">
                <label htmlFor="teacher-filter" className="block text-sm font-medium text-gray-700">
                  المدرس
                </label>
                <select
                  id="teacher-filter"
                  value={filters.teacherId}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, teacherId: e.target.value })}
                  className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                >
                  <option value="">جميع المدرسين</option>
                  {teachers.map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.fullName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Teacher Info Display - Only for Teachers */}
            {isTeacher() && user && (
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">المدرس</label>
                <div className="block w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-700">
                  {user.name}
                </div>
              </div>
            )}
          </div>
          <div className="mt-6 flex justify-end gap-4">
            <button
              onClick={handleSearch}
              className="inline-flex items-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg
                       shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2
                       focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              تطبيق التصفية
            </button>
            <button
              onClick={handleResetFilters}
              className="inline-flex items-center px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 text-sm transition-colors duration-200"
            >
              إعادة تعيين
            </button>
          </div>
        </div>

        {/* Units Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    العنوان
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    المستوى الدراسي
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    المدرس
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    عدد الدروس
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الحالة
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4">
                      <div className="flex items-center justify-center text-red-500">
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {error}
                      </div>
                    </td>
                  </tr>
                ) : units.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <p className="text-lg font-medium">لم يتم العثور على محاضرات</p>
                        <p className="text-sm">حاول تعديل التصفية للعثور على ما تبحث عنه</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  units.map((unit) => (
                    <tr 
                      key={unit.id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td 
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/lectures/${unit.id}`)}
                      >
                        <div className="text-sm font-medium text-gray-900">{unit.title}</div>
                      </td>
                      <td 
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/lectures/${unit.id}`)}
                      >
                        <div className="text-sm text-gray-600">{unit.gradeName}</div>
                      </td>
                      <td 
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/lectures/${unit.id}`)}
                      >
                        <div className="text-sm text-gray-600">{unit.teacherName}</div>
                      </td>
                      <td 
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/lectures/${unit.id}`)}
                      >
                        <div className="text-sm text-gray-600">{unit.lessons.length} درس</div>
                      </td>
                      <td 
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => navigate(`/lectures/${unit.id}`)}
                      >
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          unit.isVisible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {unit.isVisible ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-reverse space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setUnitToToggle(unit);
                            }}
                            className={`inline-flex items-center p-2 rounded-lg transition-colors duration-200 ${
                              unit.isVisible
                                ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50'
                                : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                            }`}
                            title={unit.isVisible ? 'إخفاء المحاضرة' : 'إظهار المحاضرة'}
                          >
                            {unit.isVisible ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setUnitToDelete(unit);
                            }}
                            className="inline-flex items-center p-2 text-red-600 hover:text-red-900 rounded-lg hover:bg-red-50
                                     transition-colors duration-200"
                            title="حذف المحاضرة"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

        </div>

        {/* Pagination */}
        {!loading && units.length > 0 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center bg-white rounded-2xl shadow-lg px-6 py-4">
            <div className="text-sm text-gray-700 mb-4 sm:mb-0">
              عرض <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> إلى{' '}
              <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span>{' '}
              من <span className="font-medium">{totalCount}</span> نتيجة
            </div>
            <div className="flex items-center space-x-reverse space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                  currentPage === 1
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200'
                }`}
              >
                الأول
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => {
                  return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                })
                .map((page, index, array) => {
                  const prevPage = array[index - 1];
                  if (index > 0 && prevPage && page - prevPage > 1) {
                    return [
                      <span key={`ellipsis-${prevPage}`} className="px-3 py-2">...</span>,
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium
                          ${currentPage === page
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                            : 'text-gray-700 hover:bg-gray-50 transition-colors duration-200'
                          }`}
                      >
                        {page}
                      </button>
                    ];
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium
                        ${currentPage === page
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                          : 'text-gray-700 hover:bg-gray-50 transition-colors duration-200'
                        }`}
                    >
                      {page}
                    </button>
                  );
                })}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                  currentPage === totalPages
                    ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                    : 'text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200'
                }`}
              >
                الأخير
              </button>
            </div>
          </div>
        )}

        {/* Add Unit Modal */}
        {showAddUnitModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl sm:text-2xl font-bold mb-6">إضافة محاضرة جديدة</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اسم المحاضرة *
                  </label>
                  <input
                    type="text"
                    value={newUnitTitle}
                    onChange={(e) => setNewUnitTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="أدخل اسم المحاضرة"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    العنوان الفرعي
                  </label>
                  <input
                    type="text"
                    value={newUnitSubtitle}
                    onChange={(e) => setNewUnitSubtitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="اختياري — إن تُرك فارغاً يُستخدم اسم المحاضرة"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    السعر (جنيه) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newUnitPrice}
                    onChange={(e) => setNewUnitPrice(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="مثال: 99.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    المستوى الدراسي *
                  </label>
                  <select
                    value={selectedGradeId}
                    onChange={(e) => setSelectedGradeId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">اختر المستوى الدراسي</option>
                    {grades.map((grade) => (
                      <option key={grade.id} value={grade.id}>
                        {grade.name}
                      </option>
                    ))}
                  </select>
                </div>
                {!isTeacher() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      المعلم *
                    </label>
                    <select
                      value={selectedTeacherId}
                      onChange={(e) => setSelectedTeacherId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={loadingTeachers}
                    >
                      <option value="">اختر المعلم</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.fullName}
                        </option>
                      ))}
                    </select>
                    {loadingTeachers && (
                      <p className="text-sm text-gray-500 mt-1">جاري تحميل المعلمين...</p>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    صورة المحاضرة
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      if (imagePreview) URL.revokeObjectURL(imagePreview);
                      setImageFile(file);
                      setImagePreview(file ? URL.createObjectURL(file) : null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {imagePreview && (
                    <img
                      src={imagePreview}
                      alt="معاينة"
                      className="mt-2 h-24 w-24 object-cover rounded-lg border border-gray-200"
                    />
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => {
                    setShowAddUnitModal(false);
                    resetAddUnitForm();
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleAddUnit}
                  disabled={isSaving}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {isSaving ? 'جاري الحفظ...' : 'حفظ'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {unitToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
            <div className="text-center mb-5">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <TrashIcon className="h-8 w-8 text-red-600" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">حذف المحاضرة</h3>
              <p className="text-gray-600">
                هل أنت متأكد من حذف <span className="font-bold text-gray-900">{unitToDelete.title}</span>؟
                لا يمكن التراجع عن هذا الإجراء.
              </p>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                onClick={() => setUnitToDelete(null)}
                disabled={isDeleting}
              >
                إلغاء
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-70 flex items-center gap-2"
                onClick={() => handleDeleteUnit(unitToDelete.id)}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري الحذف...
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-4 w-4" />
                    حذف
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Visibility Confirmation Modal */}
      {unitToToggle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
            <div className="text-center mb-5">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                <ExclamationTriangleIcon className="h-8 w-8 text-blue-600" aria-hidden="true" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                {unitToToggle.isVisible ? 'إخفاء المحاضرة' : 'إظهار المحاضرة'}
              </h3>
              <p className="text-gray-600">
                هل أنت متأكد من أنك تريد {unitToToggle.isVisible ? 'إخفاء' : 'إظهار'} المحاضرة <span className="font-bold text-gray-900">{unitToToggle.title}</span>؟
                {unitToToggle.isVisible ? ' هذا سيجعلها غير مرئية للطلاب.' : ' هذا سيجعلها مرئية للطلاب.'}
              </p>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                onClick={() => setUnitToToggle(null)}
              >
                إلغاء
              </button>
              <button
                className={`px-4 py-2 rounded-lg ${
                  unitToToggle.isVisible
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : 'bg-green-600 hover:bg-green-700'
                } text-white font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  unitToToggle.isVisible
                    ? 'focus:ring-yellow-500'
                    : 'focus:ring-green-500'
                }`}
                onClick={() => handleToggleVisibility(unitToToggle)}
              >
                {unitToToggle.isVisible ? 'إخفاء المحاضرة' : 'إظهار المحاضرة'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lectures;
